import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

/**
 *
 * @prop {string} value - LaTeX content value
 * @prop {function} onChange - Called when input changes with new LaTeX value
 * @prop {function} onBlur - Called when field loses focus with current LaTeX value
 * @prop {function} onClick - Custom click handler (receives click event)
 * @prop {Object} style - Custom styles for the wrapper div
 * @prop {string} className - Additional CSS classes
 * @prop {string} placeholder - Placeholder text for the math field use \text{...}
 *
 * Keyboard Controls:
 * @prop {boolean} readonly - Whether input is disabled
 * @prop {boolean} keyboardEnabled - Whether virtual keyboard is enabled at all
 * @prop {string} keyboardMode - 'auto', 'manual', 'onfocus', or 'off'
 * @prop {string} keyboardLayout - numeric, symbols, alphabetic, greek, minimalist, compact, numeric-only.
 * @prop {boolean} hideOnBlur - Auto-hide keyboard when focus is lost
 * @prop {boolean} blockPhysicalKeyboard - Prevent input from physical keyboard
 *
 * Advanced:
 * @prop {Object} options - Direct MathLive options (will override other props if conflicts exist)
 * to apply styles to mathfield use options.style - ex options={{ style: { fontSize: "1.5em" }}}
 */

const MathKeyboard = forwardRef((props, ref) => {
  const {
    value = "",
    onChange,
    onBlur,
    onClick,
    style = {},
    className = "",
    placeholder = "\\text{Enter formula}",

    readonly = false,
    keyboardMode = "manual",
    keyboardLayout = "default",
    hideOnBlur = true,
    blockPhysicalKeyboard = true,

    options = {},
  } = props;

  const disabled = readonly || options?.disabled;

  const wrapperRef = useRef(null);
  const mathfieldRef = useRef(null);

  // Expose methods to parent components through ref
  useImperativeHandle(ref, () => ({
    focus: () => mathfieldRef.current?.focus(),
    blur: () => mathfieldRef.current?.blur(),
    getValue: () => mathfieldRef.current?.value || "",
    setValue: (latex) => {
      if (mathfieldRef.current) mathfieldRef.current.value = latex;
    },
    insert: (latex) => mathfieldRef.current?.insert(latex),
    insertText: (text) => mathfieldRef.current?.executeCommand("insert", text),
    select: () => mathfieldRef.current?.select(),
    showKeyboard: () => {
      if (!disabled && window.mathVirtualKeyboard) {
        window.mathVirtualKeyboard.show();
      }
    },
    hideKeyboard: () => {
      if (window.mathVirtualKeyboard) {
        window.mathVirtualKeyboard.hide();
      }
    },
    toggleKeyboard: () => {
      if (!disabled && window.mathVirtualKeyboard) {
        const isVisible = window.mathVirtualKeyboard.visible;
        if (isVisible) {
          window.mathVirtualKeyboard.hide();
        } else {
          window.mathVirtualKeyboard.show();
        }
      }
    },
    isKeyboardVisible: () => window.mathVirtualKeyboard?.visible || false,
    getMathfield: () => mathfieldRef.current,
  }));

  // Set up MathLive mathfield when component mounts
  useEffect(() => {
    if (!wrapperRef.current) return;

    const mf = wrapperRef.current.querySelector("math-field");
    if (!mf) return;

    mathfieldRef.current = mf;

    // Set initial value
    if (mf.value !== value) {
      mf.value = value;
    }

    // Configure mathfield options
    const config = {
      readOnly: readonly,
      smartSuperscript: true,
      mathVirtualKeyboardPolicy: keyboardMode,
      virtualKeyboardTargetOrigin: "*",
      mathModeSpace: "\\:",
      placeholder: placeholder || options.placeholder,
      ...options,
    };

    if (config.style) {
      delete config.style; // Remove style from config to avoid conflicts
    }

    window.MathfieldElement.soundsDirectory = null;
    window.MathfieldElement.keypressVibration = false;

    window.mathVirtualKeyboard.layouts = keyboardLayout;
    window.mathVirtualKeyboard.targetOrigin = "*";

    Object.entries(config).forEach(([k, v]) => {
      mf[k] = v;
    });

    // Event handlers
    const handleInput = () => onChange?.(mf.value);
    const handleBlur = () => {
      onBlur?.(mf.value);
      if (hideOnBlur && window.mathVirtualKeyboard) {
        window.mathVirtualKeyboard.hide();
      }
    };

    const handleFocus = () => {
      if (window.mathVirtualKeyboard && !disabled) {
        window.mathVirtualKeyboard.show();
      }
    };

    const handleClick = (ev) => {
      onClick?.(ev);
      if (!disabled) {
        ev.target.focus();
        window.mathVirtualKeyboard?.show();
      }
    };

    const handleKeystroke = (evt) => {
      evt.preventDefault();
      return false;
    };

    mf.addEventListener("input", handleInput);
    mf.addEventListener("blur", handleBlur);
    mf.addEventListener("focusin", handleFocus);
    mf.addEventListener("focusout", handleBlur);
    mf.addEventListener("click", handleClick);

    if (blockPhysicalKeyboard) {
      mf.addEventListener("keydown", handleKeystroke, { capture: true });
    }
    return () => {
      mf.removeEventListener("keydown", handleKeystroke, { capture: true });
      mf.removeEventListener("input", handleInput);
      mf.removeEventListener("blur", handleBlur);
      mf.removeEventListener("focusin", handleFocus);
      mf.removeEventListener("focusout", handleBlur);
      mf.removeEventListener("click", handleClick);
    };
  }, [
    value,
    keyboardMode,
    keyboardLayout,
    options,
    blockPhysicalKeyboard,
    hideOnBlur,
    onChange,
    onBlur,
    onClick,
    disabled,
    readonly,
    placeholder,
  ]);

  return (
    <div
      ref={wrapperRef}
      className={`mathlive-editor-wrapper ${className}`}
      style={{ width: "100%", ...style }}
    >
      <math-field
        style={{ width: "100%", ...(options?.style ? options?.style : {}) }}
        placeholder={placeholder || options.placeholder}
      />
    </div>
  );
});

MathKeyboard.displayName = "MathKeyboard";

export default MathKeyboard;
